import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { hotelSearch } from '../../../../../store/slices/hotel';
import MultiSelectAutocompleteFilter from '../../../../Filters/MultiSelectAutocompleteFilter';
import {
  hotelSearchRequest,
  setHotelTripSearchInputs,
} from '../../../../../store/slices/tripSearch';
import hotelSearchInput from '../../../../../helpers/selectors/hotelSearchInput';

const filter = createFilterOptions();

const HotelChainsFilter = ({ tripId, variant, filterSpacingClass }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;

  const searchStateHotelChains = useSelector((store) =>
    !isTripSearch
      ? store.hotel?.hotelSearches[tripId]?.hotelChainsResults || {}
      : store.tripSearch?.searchResult?.filters?.hotelChains || {},
  );

  const { searchInputs: hotelSearchInputs = {}, searchObj: hotelSearchObj = {} } = useSelector(
    hotelSearchInput(tripId, isTripSearch),
  );

  const searchObjChains = isTripSearch
    ? hotelSearchInputs.filterHotelChains
    : hotelSearchObj.filterHotelChains;

  const [selectedSet, setSelectedSet] = useState(() => new Set(searchObjChains || []));

  useEffect(() => {
    setSelectedSet(new Set(searchObjChains || []));
  }, [searchObjChains]);

  const groups = useMemo(() => {
    const result = {};
    Object.entries(searchStateHotelChains).forEach(([chain, marketer]) => {
      result[marketer] ??= [];
      result[marketer].push({ value: chain, label: chain });
    });
    return result;
  }, [searchStateHotelChains]);

  const hotelChainsOptions = useMemo(() => {
    const options = Object.keys(groups)
      .sort()
      .map((marketer) => ({
        label: marketer,
        suggests: groups[marketer].sort((a, b) => a.label.localeCompare(b.label)),
      }));
    return options;
  }, [groups]);

  const value = useMemo(() => {
    const allSuggestions = Object.values(groups).flat();
    const result = allSuggestions.filter((opt) => selectedSet.has(opt.value));
    return result;
  }, [groups, selectedSet]);

  const buildSearchPayload = useCallback(
    (newSet) => ({
      page: 1,
      filterHotelChains: Array.from(newSet),
      hasFilterApplied: true,
    }),
    [],
  );

  const triggerSearch = useCallback(
    (newSet) => {
      const searchObj = buildSearchPayload(newSet);

      if (isTripSearch) {
        dispatch(setHotelTripSearchInputs(searchObj));
        dispatch(hotelSearchRequest(searchObj));
      } else {
        dispatch(hotelSearch({ tripId, searchObj }));
      }

      track('HotelChainsFilter.triggerSearch', {
        payload: JSON.stringify({ tripId, searchObj }),
      });
    },
    [tripId, isTripSearch, dispatch, track, buildSearchPayload],
  );

  const handleChange = useCallback(
    (newValue = []) => {
      const newSet = new Set(newValue.map((v) => v.value));
      setSelectedSet(newSet);
      triggerSearch(newSet);
    },
    [triggerSearch],
  );

  const handleMarketerToggle = useCallback(
    (marketer) => {
      const chains = groups[marketer]?.map((c) => c.value);
      if (!chains?.length) return;

      const next = new Set(selectedSet);
      const allSelected = chains.every((id) => next.has(id));
      chains.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));

      setSelectedSet(next);
      triggerSearch(next);
    },
    [groups, selectedSet, triggerSearch],
  );

  const renderGroup = (params) => (
    <li key={params.key}>
      <button
        type="button"
        className="w-full text-left px-4 py-2 text-sm font-bold hover:bg-gray-100"
        onClick={(e) => {
          e.stopPropagation();
          handleMarketerToggle(params.group);
        }}
      >
        {params.group}
      </button>
      <ul className="pl-4">{params.children}</ul>
    </li>
  );

  const filterOptions = (options, params) => {
    const filtered = filter(options, params);
    if (params.inputValue) {
      const search = params.inputValue.toLowerCase();
      options.forEach((option) => {
        if (
          option.group?.toLowerCase().includes(search) &&
          !filtered.some((f) => f.value === option.value)
        ) {
          filtered.push(option);
        }
      });
    }
    return filtered;
  };

  return hotelChainsOptions && hotelChainsOptions.length > 0 ? (
    <MultiSelectAutocompleteFilter
      label="Hotel Chains"
      options={hotelChainsOptions}
      values={value}
      onChange={handleChange}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      filterMaxWidthClass="max-w-full"
      variant={variant}
      filterSpacingClass={filterSpacingClass}
      bgClassName={!isTripSearch ? '' : undefined}
      freeSolo={false}
      openOnFocus
      renderGroup={renderGroup}
      filterOptions={filterOptions}
      getOptionSelected={(option, v) => option.value === v.value}
    />
  ) : (
    <></>
  );
};

HotelChainsFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterSpacingClass: PropTypes.string,
};

HotelChainsFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterSpacingClass: undefined,
};

export default HotelChainsFilter;
